import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BRAND = '8080.AI';

const PAGE_TITLES: Record<string, string> = {
  '/': `${BRAND} — Build with AI Agents`,
  '/pricing': `Pricing | ${BRAND}`,
  '/privacy': `Privacy Policy | ${BRAND}`,
  '/terms': `Terms & Conditions | ${BRAND}`,
  '/profile': `Profile | ${BRAND}`,
  '/planning': `Planning | ${BRAND}`,
  '/projects': `Projects | ${BRAND}`,
  '/billing': `Billing | ${BRAND}`,
  '/team': `Teams | ${BRAND}`,
  '/team/analytics': `Team Analytics | ${BRAND}`,
  '/admin/settings': `Admin Settings | ${BRAND}`,
  '/admin/projects': `Admin Projects | ${BRAND}`,
  '/admin/users': `Admin Users | ${BRAND}`,
  '/admin/monthly-invoices': `Monthly Invoices | ${BRAND}`,
  '/admin/traffic-analysis': `Traffic Analysis | ${BRAND}`,
  '/admin/performance-analytics': `Performance Analytics | ${BRAND}`,
  '/admin/models': `Model Settings | ${BRAND}`,
  '/admin/model-logs': `Model Logs | ${BRAND}`,
  '/admin/loop-logs': `Loop Logs | ${BRAND}`,
  '/admin/artifact-logs': `Artifact Logs | ${BRAND}`,
  '/admin/proposal-logs': `Proposal Logs | ${BRAND}`,
  '/admin/workspace-task-logs': `Workspace Task Logs | ${BRAND}`,
  '/admin/chat-logs': `Chat Logs | ${BRAND}`,
  '/admin/page-quality-review-logs': `Page Quality Review Logs | ${BRAND}`,
  '/admin/model-analytics': `Model Analytics | ${BRAND}`,
  '/auth/reset-password': `Reset Password | ${BRAND}`,
  '/auth/verify-email': `Verify Email | ${BRAND}`,
};

function resolveTitle(pathname: string): string {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith('/planning/')) return `Planning | ${BRAND}`;
  if (pathname.startsWith('/projects/') && pathname.endsWith('/team'))
    return `Team | ${BRAND}`;
  if (pathname.startsWith('/invite/')) return `Invitation | ${BRAND}`;
  if (pathname.startsWith('/organization-invite/')) return `Organization Invitation | ${BRAND}`;

  return `${BRAND} — Build with AI Agents`;
}

export const isAdminPath = (pathname: string): boolean => pathname === '/admin' || pathname.startsWith('/admin/');

/** Team pages (leads' and recipients' details) and the public document viewer: untracked, like admin pages. */
export const isPrivateDataPath = (pathname: string): boolean => (
  /^\/(marketing|operations|sales)(\/|$)/.test(pathname) || pathname.startsWith('/d/')
);

/**
 * index.html loads no analytics or marketing vendor on admin and private-data pages, but a session that started
 * elsewhere already has them running (Hotjar records, pixels see every history change). Entering such a page in
 * that session reloads it once, so the page loads without them. Returns whether it reloaded.
 */
export function leaveTrackedSessionForAdmin(
  pathname: string,
  reload: () => void = () => window.location.reload(),
): boolean {
  if (!(isAdminPath(pathname) || isPrivateDataPath(pathname)) || !window.__load8080Analytics) return false;
  reload();

  return true;
}

export const usePageTracking = (): void => {
  const location = useLocation();

  useEffect(() => {
    if (leaveTrackedSessionForAdmin(location.pathname)) return;
    const title = resolveTitle(location.pathname);

    document.title = title;
    // GA4 Enhanced Measurement is the single page-view authority. It observes
    // browser-history changes for this SPA; emitting another page_view here
    // duplicated every navigation and could run before the Google tag config.
  }, [location.pathname]);
};
